import Search16 from '@carbon/icons-react/es/search/16';
import { Popover } from 'antd';
import { TooltipPlacement } from 'antd/es/tooltip';
import moment, { ISO_8601 } from 'moment';
import React, { Dispatch, FC, SetStateAction } from 'react';
import { FormattedMessage } from 'react-intl';
import { components as RsComponents } from 'react-select';
import styled from 'styled-components';
import { FormTemplate } from 'api/form';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { RelativeTime } from 'components/atoms/relative-time';
import { Select } from 'components/atoms/select';
import {
  FormOption,
  VirtualizedMenuList,
} from 'components/atoms/select/custom-components';
import { Caption01 } from 'components/styles';
import styles from './form-components.module.css';

const LastSubmittedByText = styled.div`
  ${Caption01};
  color: var(--gray-70);
`;

export const LastEditedBy = ({
  submittedAt,
  submittedBy,
}: {
  submittedAt?: number;
  submittedBy?: string;
}): JSX.Element => {
  return (
    <LastSubmittedByText>
      <FormattedMessage
        id="forms.response.last_submitted_by"
        values={{
          by: submittedBy || <FormattedMessage id="anonymous" />,
          time: <RelativeTime time={moment(submittedAt, ISO_8601)} />,
        }}
      />
    </LastSubmittedByText>
  );
};

const SelectContainer = styled.div`
  min-height: 350px;
  width: 272px;
  display: flex;
  flex-direction: column;
  .ozone-select__control {
    margin: var(--spacing-05);
    &:focus {
      border-color: var(--primary-color);
    }
  }
  .ozone-select__option {
    padding-left: var(--spacing-05);
    padding-right: var(--spacing-05);
  }
`;

const StyledPopover = styled(Popover)`
  .ant-popover-inner-content {
    padding: 0px;
  }
`;
const SearchFormDropdownIndicator = (props) => {
  return (
    <RsComponents.DropdownIndicator {...props}>
      <Search16 />
    </RsComponents.DropdownIndicator>
  );
};
const selectStyles = {
  control: (provided) => ({
    ...provided,
    margin: 4,
  }),
  menu: () => ({ boxShadow: 'none' }),
};

interface Props {
  visible: boolean;
  setVisible: Dispatch<SetStateAction<boolean>>;
  formTemplates: FormTemplate[] | undefined;
  isLoading: boolean;
  createForm: (v) => void;
  placement?: TooltipPlacement;
}
export const AddFormTemplatePopover: FC<Props> = ({
  children,
  visible,
  formTemplates,
  isLoading,
  setVisible,
  createForm,
  placement,
}): JSX.Element => {
  return (
    <StyledPopover
      placement={placement ?? 'right'}
      trigger={['click']}
      visible={visible}
      destroyTooltipOnHide
      title={<FormattedMessage id="forms" />}
      overlayClassName={styles.overlay}
      content={
        <OutsideClickHandler
          onOutsideClick={() => {
            setVisible(false);
          }}
        >
          <SelectContainer data-test-id="form-picker">
            <Select
              menuIsOpen
              autoFocus
              hideSelectedOptions={false}
              controlShouldRenderValue={false}
              components={{
                IndicatorSeparator: () => null,
                DropdownIndicator: SearchFormDropdownIndicator,
                Option: FormOption,
                MenuList: VirtualizedMenuList,
              }}
              maxMenuHeight={350}
              styles={selectStyles}
              placeholder={<FormattedMessage id="forms.response.search_form" />}
              options={(formTemplates || []).map((template) => {
                return {
                  label: template.name,
                  value: template.templateId,
                };
              })}
              isLoading={isLoading}
              onChange={(v) => createForm(v)}
            />
          </SelectContainer>
        </OutsideClickHandler>
      }
    >
      {children}
    </StyledPopover>
  );
};
